import { COPY, DEFAULT_LANGUAGE, isLanguage, type Copy, type Language } from './i18n';
import { useBrandState } from './brand';

/**
 * What this page takes from the tenant's brand: the language it speaks, and the line it opens with.
 *
 * Since 2026-10-05 both come from `brand`'s open `GET /v1/public` (`./brand.ts`), the route every
 * public page reads — `default_language` and `tagline`. Until then this app asked its own
 * `GET /v1/config`, because the brand's open route did not exist when the page was written. English
 * shows until the brand lands (or from this origin's cache on a return visit).
 *
 * `Login` and `useLogin` both call it; `useBrand` memoises the one request at module scope.
 */
export interface UseLanguageResult {
  language: Language;
  copy: Copy;
  /**
   * The tenant's own line, or `''`. Empty keeps the fleet's two-line epigraph — a good line that is
   * nobody's brand.
   */
  slogan: string;
  /**
   * Whether `language` is final: the brand came from cache or its request finished. Google's
   * sign-in button is drawn in the page's language and REBUILT when it changes, so the page waits
   * for this before drawing it (2026-10-31: it was drawn in English, then again in Spanish).
   */
  settled: boolean;
}

export const useLanguage = (): UseLanguageResult => {
  const { brand, settled } = useBrandState();
  const wanted = String(brand?.default_language ?? '')
    .trim()
    .toLowerCase()
    .slice(0, 2);
  const language: Language = isLanguage(wanted) ? wanted : DEFAULT_LANGUAGE;
  return {
    language,
    copy: COPY[language],
    slogan: String(brand?.tagline ?? '').trim(),
    settled,
  };
};
